import { map, tap } from "rxjs"
import { normalizeApiDate } from "../../shared/utils/api-date"
import { HttpClient, HttpParams } from "@angular/common/http"
import { inject, Injectable } from "@angular/core"
import { environment } from "../../../../environments/environment"
import { NO_ERROR_HANDLING } from "../../shared/constants/strings"
import {
  IAggregateInsightsResponse,
  IPersonalInsightsResponse,
  InsightPeriod,
} from "../interfaces/insight.interface"

@Injectable({ providedIn: "root" })
export class InsightService {
  private readonly http = inject(HttpClient)
  private readonly baseUrl = `${environment.api.baseUrl}/RMBTControlServer/insights`
  private readonly options = { headers: { [NO_ERROR_HANDLING]: "true" } }

  // In-memory snapshots prevent empty/loading flashes when returning to a page.
  // Requests still run on every visit; snapshots are isolated by device ID.
  private readonly personalSnapshots = new Map<string, IPersonalInsightsResponse>()

  getPersonalSnapshot(clientId: string, limit = 20): IPersonalInsightsResponse | null {
    const snapshot = this.personalSnapshots.get(clientId)
    return snapshot ? { ...snapshot, tests: snapshot.tests.slice(0, limit) } : null
  }

  getPersonal(clientId: string, limit = 20, offset = 0) {
    const params = new HttpParams()
      .set("client_id", clientId)
      .set("limit", limit)
      .set("offset", offset)
    return this.http.get<IPersonalInsightsResponse>(`${this.baseUrl}/personal`, {
      ...this.options,
      params,
    }).pipe(map((response) => ({
      ...response,
      tests: response.tests.map((test) => ({ ...test, measured_at: normalizeApiDate(test.measured_at) })),
    })), tap((response) => {
      if (offset !== 0) return
      const previous = this.personalSnapshots.get(clientId)
      // A Home refresh requests only one row. Keep already-loaded history if
      // the first row still matches, while refreshing that row and the total.
      const keepHistory = previous?.tests[0]?.test_id === response.tests[0]?.test_id
        && (previous?.tests.length ?? 0) > response.tests.length
      this.personalSnapshots.set(clientId, {
        ...response,
        tests: keepHistory
          ? [...response.tests, ...previous!.tests.slice(response.tests.length)].slice(0, response.total)
          : response.tests,
      })
    }))
  }

  getAggregate(period: InsightPeriod, spotId?: number) {
    let params = new HttpParams().set("period", period)
    if (spotId != null) params = params.set("spot_id", spotId)
    return this.http.get<IAggregateInsightsResponse>(`${this.baseUrl}/aggregate`, {
      ...this.options,
      params,
    }).pipe(map((response) => ({ ...response, generated_at: normalizeApiDate(response.generated_at) })))
  }
}
